/**
 * Everything a cutscene will reach for, derived from its tick list rather
 * than kept as a hand-maintained sidecar — the same idea `shell/intro/
 * intro-cues.ts` used for `TITLE.TTM` alone, generalised so any script's
 * ticks (plus the songs it is known to cue, since `SELECT SONG` is a silent
 * state setter the interpreter never surfaces as a command — see
 * `cutscene-player.ts`'s header) produce the same shape.
 */

import type { TtmCommand } from "@red-opus/formats";
import { MUSIC_PATHS, sfxPath } from "../audio/shell-audio.ts";
import type { CutsceneTick } from "./cutscene-ticks.ts";

export interface CutscenePreloadSet {
  /** Baked image ids (no `.png`) for every background and sprite sheet the script draws. */
  readonly images: readonly string[];
  /** The subset of `images` that also needs frame rectangles out of the bake manifest. */
  readonly sprites: readonly string[];
  /** `.PAL` filenames the script loads, for `ShellAssetRequest.palettes`. */
  readonly palettes: readonly string[];
  /** `/baked` URLs for both songs and every sound-effect cue. */
  readonly audio: readonly string[];
}

/** `PLAY SFX n` indexes whichever bank the last `SELECT SND SLOT` chose, so the walk has to carry the slot. */
function collect(ticks: readonly CutsceneTick[], visit: (command: TtmCommand, slot: number) => void): void {
  let slot = 0;
  for (const tick of ticks) {
    for (const command of tick.commands) {
      if (command.kind === "selectSoundSlot") slot = command.slot;
      visit(command, slot);
    }
  }
}

/** `songs` are the `MUSIC_PATHS` keys this script is known to cue — see this module's header for why that can't be derived from the commands alone. */
export function cutscenePreloadSet(ticks: readonly CutsceneTick[], songs: readonly string[]): CutscenePreloadSet {
  const screens = new Set<string>();
  const sprites = new Set<string>();
  const palettes = new Set<string>();
  const audio = new Set<string>();
  for (const song of songs) {
    const path = MUSIC_PATHS[song];
    if (path) audio.add(path);
  }
  collect(ticks, (command, slot) => {
    if (command.kind === "loadScreen") screens.add(`screens/${command.id}`);
    else if (command.kind === "drawSprite" && command.id !== undefined) sprites.add(`sprites/${command.id}`);
    else if (command.kind === "loadPalette") palettes.add(command.id);
    else if (command.kind === "playSound") audio.add(sfxPath(slot, command.id));
  });
  return { images: [...screens, ...sprites], sprites: [...sprites], palettes: [...palettes], audio: [...audio] };
}
